import { buildMsal, apiScopes } from "../auth/msalConfig";
import { config } from "../config";
import type {
  MeResponse,
  AssessmentSummary,
  AssessmentDetail,
  CreateAssessmentInput,
  CreatedAssessment,
  EvalLookup,
  QuestionAdmin,
  LookupRow,
  RoleRow,
  SettingRow,
  QuizPayload,
  QuizStartResponse,
} from "./types";

async function getToken(): Promise<string> {
  const msal = buildMsal();
  const account = msal.getAllAccounts()[0];
  if (!account) throw new Error("Not signed in");
  const result = await msal.acquireTokenSilent({ scopes: apiScopes(), account });
  return result.accessToken;
}

async function parseError(res: Response): Promise<string> {
  let detail = res.statusText;
  try {
    const j = await res.json();
    detail = j.detail ?? j.title ?? detail;
  } catch {
    /* ignore */
  }
  return `${res.status}: ${detail}`;
}

async function call<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${config().api_base}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) throw new Error(await parseError(res));
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Authenticated binary fetch (report PDF) — returns an object URL. */
async function callBlob(path: string): Promise<string> {
  const token = await getToken();
  const res = await fetch(`${config().api_base}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await parseError(res));
  return URL.createObjectURL(await res.blob());
}

/** Unauthenticated candidate calls (token lives in the path). */
async function candidateCall<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${config().api_base}${path}`, {
    ...init,
    headers: { ...init.headers, "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export const api = {
  me: () => call<MeResponse>("/api/me"),
  lookups: () => call<Record<string, Array<{ id: number; value: string }>>>("/api/lookups"),

  assessments: {
    list: (params: Record<string, string>) => {
      const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== ""));
      return call<AssessmentSummary[]>(`/api/assessments?${qs.toString()}`);
    },
    create: (body: CreateAssessmentInput) =>
      call<CreatedAssessment>("/api/assessments", { method: "POST", body: JSON.stringify(body) }),
    get: (id: string) => call<AssessmentDetail>(`/api/assessments/${id}`),
    remove: (id: string) => call<void>(`/api/assessments/${id}`, { method: "DELETE" }),
    resendLink: (id: string) =>
      call<{ candidate_url: string; expires_in_days: number }>(`/api/assessments/${id}/resend-link`, { method: "POST" }),
    byTestId: (testId: string) => call<EvalLookup>(`/api/assessments/by-test-id/${encodeURIComponent(testId)}`),
    evaluate: (id: string, body: { result: "satisfied" | "dissatisfied"; comment?: string; force?: boolean }) =>
      call<AssessmentDetail>(`/api/assessments/${id}/evaluate`, { method: "POST", body: JSON.stringify(body) }),
    reportUrl: (id: string) => callBlob(`/api/assessments/${id}/report`),
  },

  manage: {
    questions: {
      list: () => call<QuestionAdmin[]>("/api/manage/questions"),
      create: (q: Omit<QuestionAdmin, "id" | "sp_id">) =>
        call<{ id: number }>("/api/manage/questions", { method: "POST", body: JSON.stringify(q) }),
      update: (id: number, q: Omit<QuestionAdmin, "id" | "sp_id">) =>
        call<{ id: number }>(`/api/manage/questions/${id}`, { method: "PUT", body: JSON.stringify(q) }),
      deactivate: (id: number) => call<void>(`/api/manage/questions/${id}`, { method: "DELETE" }),
    },
    lookups: {
      list: () => call<LookupRow[]>("/api/manage/lookups"),
      create: (l: Omit<LookupRow, "id">) => call<{ id: number }>("/api/manage/lookups", { method: "POST", body: JSON.stringify(l) }),
      update: (id: number, l: Omit<LookupRow, "id">) =>
        call<{ id: number }>(`/api/manage/lookups/${id}`, { method: "PUT", body: JSON.stringify(l) }),
      deactivate: (id: number) => call<void>(`/api/manage/lookups/${id}`, { method: "DELETE" }),
      remove: (id: number) => call<void>(`/api/manage/lookups/${id}?hard=1`, { method: "DELETE" }),
      reactivate: (id: number, l: Omit<LookupRow, "id">) =>
        call<{ id: number }>(`/api/manage/lookups/${id}`, { method: "PUT", body: JSON.stringify({ ...l, active: true }) }),
    },
    roles: {
      list: () => call<RoleRow[]>("/api/manage/roles"),
      add: (email: string, role: "auditor" | "admin" | "manager" | "recruitment", home?: string) =>
        call<{ id: number | null }>("/api/manage/roles", { method: "POST", body: JSON.stringify({ email, role, home }) }),
      remove: (id: number) => call<void>(`/api/manage/roles/${id}`, { method: "DELETE" }),
    },
    settings: {
      list: () => call<SettingRow[]>("/api/manage/settings"),
      set: (key: string, value: string) =>
        call<{ key: string; value: string }>(`/api/manage/settings/${key}`, { method: "PUT", body: JSON.stringify({ value }) }),
    },
  },

  candidate: {
    get: (token: string) => candidateCall<QuizPayload>(`/api/take/${token}`),
    start: (token: string, body: { consent: boolean; declaration: boolean; client: Record<string, string> }) =>
      candidateCall<QuizStartResponse>(`/api/take/${token}/start`, { method: "POST", body: JSON.stringify(body) }),
    postEvents: (token: string, events: Array<{ kind: string; detail?: Record<string, string | number | boolean> }>) =>
      candidateCall<{ ok: boolean }>(`/api/take/${token}/events`, {
        method: "POST",
        body: JSON.stringify({ events }),
      }).catch(() => ({ ok: false })),
    submit: (
      token: string,
      answers: Array<{ question_id: number; answer: string | null }>,
      integrity?: { focus_loss_count?: number; paste_count?: number },
    ) =>
      candidateCall<{ ok: boolean; first_name: string }>(`/api/take/${token}/submit`, {
        method: "POST",
        body: JSON.stringify({ answers, integrity }),
      }),
  },
};
